import { computed, Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import {
	shouldPublishPageFilter,
	type PageCardFilterInput,
	type PageCardFilterSelection,
} from '@framework/analytics/models/page-filter.model';
import { extractSearchQueryFromStashedFilters } from '@framework/analytics/utils/filter-data-source.util';

interface PageFilterStash {
	dropdown: Map<string, PageCardFilterSelection>;
	search: Map<string, PageCardFilterInput[]>;
}

@Injectable({ providedIn: 'root' })
export class PageFilterService {
	private readonly _selections = signal<Map<string, PageCardFilterSelection>>(
		new Map()
	);

	private readonly _searchPageFilters = signal<
		Map<string, PageCardFilterInput[]>
	>(new Map());

	/** Reactive read of stashed search filters (tab restore, debounced publish). */
	readonly searchPageFilters = this._searchPageFilters.asReadonly();

	/** Stashed dropdown/search selections keyed by report page id (tab switch). */
	private readonly selectionsByPageId = new Map<string, PageFilterStash>();

	private activePageId: string | null = null;

	readonly refresh$ = new Subject<void>();

	/** Emitted when a search filter input changes, before debounced publish. */
	readonly refreshIntent$ = new Subject<void>();

	signalRefreshIntent(): void {
		this.refreshIntent$.next();
	}

	readonly pageFiltersForQuery = computed((): PageCardFilterInput[] => {
		const result: PageCardFilterInput[] = [];
		for (const sel of this._selections().values()) {
			if (!shouldPublishPageFilter(sel.values, sel.allOptionValues)) {
				continue;
			}
			result.push({
				cardId: sel.cardId,
				member: sel.member,
				operator: sel.operator,
				values: sel.values,
				dataSource: sel.dataSource,
			});
		}
		for (const filters of this._searchPageFilters().values()) {
			result.push(...filters);
		}
		return result;
	});

	setSelection(selection: PageCardFilterSelection): void {
		const prev = this._selections().get(selection.cardId);
		if (prev && !this.#selectionQueryChanged(prev, selection)) {
			return;
		}

		const next = new Map(this._selections());
		next.set(selection.cardId, selection);
		this._selections.set(next);
		this.#syncActivePageStash();
		this.refresh$.next();
	}

	setSearchPageFilters(
		cardId: string,
		filters: PageCardFilterInput[]
	): void {
		const prev = this._searchPageFilters().get(cardId) ?? [];
		if (!this.#searchFiltersChanged(prev, filters)) {
			return;
		}

		const next = new Map(this._searchPageFilters());
		if (filters.length === 0) {
			next.delete(cardId);
		} else {
			next.set(cardId, filters);
		}
		this._searchPageFilters.set(next);
		this.#syncActivePageStash();
		this.refresh$.next();
	}

	/** Whether the selection would change `pageFiltersForQuery` for this card. */
	#selectionQueryChanged(
		prev: PageCardFilterSelection,
		next: PageCardFilterSelection
	): boolean {
		const prevActive = shouldPublishPageFilter(
			prev.values,
			prev.allOptionValues
		);
		const nextActive = shouldPublishPageFilter(
			next.values,
			next.allOptionValues
		);
		if (prevActive !== nextActive) {
			return true;
		}
		if (!nextActive) {
			return false;
		}
		return (
			prev.member !== next.member ||
			prev.operator !== next.operator ||
			prev.dataSource !== next.dataSource ||
			!this.#valuesEqual(prev.values, next.values)
		);
	}

	#searchFiltersChanged(
		prev: PageCardFilterInput[],
		next: PageCardFilterInput[]
	): boolean {
		if (prev.length !== next.length) {
			return true;
		}
		const sortedPrev = [...prev].sort((a, b) =>
			`${a.member}:${a.operator}`.localeCompare(
				`${b.member}:${b.operator}`
			)
		);
		const sortedNext = [...next].sort((a, b) =>
			`${a.member}:${a.operator}`.localeCompare(
				`${b.member}:${b.operator}`
			)
		);
		return sortedNext.some((filter, index) => {
			const previous = sortedPrev[index];
			return (
				!previous ||
				previous.cardId !== filter.cardId ||
				previous.member !== filter.member ||
				previous.operator !== filter.operator ||
				previous.dataSource !== filter.dataSource ||
				!this.#valuesEqual(previous.values, filter.values)
			);
		});
	}

	#valuesEqual(a: string[], b: string[]): boolean {
		if (a.length !== b.length) {
			return false;
		}
		const sortedA = [...a].sort();
		const sortedB = [...b].sort();
		return sortedA.every((value, index) => value === sortedB[index]);
	}

	clearSelection(cardId: string): void {
		const nextSelections = new Map(this._selections());
		const nextSearch = new Map(this._searchPageFilters());
		const removedDropdown = nextSelections.delete(cardId);
		const removedSearch = nextSearch.delete(cardId);
		if (!removedDropdown && !removedSearch) {
			return;
		}
		this._selections.set(nextSelections);
		this._searchPageFilters.set(nextSearch);
		this.#syncActivePageStash();
		this.refresh$.next();
	}

	/**
	 * Scope filter selections to a report page/tab.
	 * Stashes the previous page's selections and restores this page's stash so
	 * dropdown UI and `getPageData` pageFilters stay aligned after tab switches
	 * (JEP-10140).
	 *
	 * - `emitRefresh: true` / omitted — always emit `refresh$` (shared report).
	 * - `emitRefresh: false` — emit only when the restored page has active filters
	 *   (preview/edit: avoid blank refetch when there is nothing to re-apply).
	 */
	activatePage(
		pageId: string,
		options?: { emitRefresh?: boolean }
	): void {
		const nextId = pageId?.trim() ?? '';
		if (!nextId) {
			return;
		}

		const prevId = this.activePageId;
		if (prevId && prevId !== nextId) {
			this.selectionsByPageId.set(prevId, this.#snapshotStash());
		}

		if (prevId === nextId) {
			return;
		}

		this.activePageId = nextId;
		const restored = this.selectionsByPageId.get(nextId);
		this._selections.set(
			restored ? new Map(restored.dropdown) : new Map()
		);
		this._searchPageFilters.set(
			restored ? new Map(restored.search) : new Map()
		);

		if (options?.emitRefresh === false) {
			if (this.hasActiveFilters()) {
				this.refresh$.next();
			}
			return;
		}

		this.refresh$.next();
	}

	#snapshotStash(): PageFilterStash {
		return {
			dropdown: new Map(this._selections()),
			search: new Map(this._searchPageFilters()),
		};
	}

	#syncActivePageStash(): void {
		if (!this.activePageId) {
			return;
		}
		this.selectionsByPageId.set(this.activePageId, this.#snapshotStash());
	}

	clear(options?: { emitRefresh?: boolean }): void {
		const hadSelections =
			this._selections().size > 0 ||
			this._searchPageFilters().size > 0;
		const hadStash = this.selectionsByPageId.size > 0;
		this._selections.set(new Map());
		this._searchPageFilters.set(new Map());
		this.selectionsByPageId.clear();
		this.activePageId = null;
		if (!hadSelections && !hadStash) {
			return;
		}
		if (options?.emitRefresh !== false) {
			this.refresh$.next();
		}
	}

	hasActiveFilters(): boolean {
		return this.pageFiltersForQuery().length > 0;
	}

	getActivePageId(): string | null {
		return this.activePageId;
	}

	getSearchQueryForCard(cardId: string): string {
		return extractSearchQueryFromStashedFilters(
			this._searchPageFilters().get(cardId) ?? []
		);
	}
}
